import { WorkBook } from '@sheet/edit';
import isMobilePhone from 'validator/lib/isMobilePhone';

import { findLatestExcelDataRowNumber, numberToColumnName } from '@app/libs/excel';
import { removeZeroWidthWhiteSpace } from '@app/libs/inputFormat';

import {
  addressExcelForm,
  DATA_START_ROW,
  ERROR_TOO_MUCH_ROWS,
  EXCEL_FORM_ERROR_MESSAGE,
  FormValue,
  HEADER_ROW_COUNT,
} from './AddressBookExcelForm';

/**
 * 엑셀에서 컬럼 타입에 따라 알맞게 값을 정제한 후 반환합니다.
 * @param workBook
 */
export default function createFormValues(workBook: WorkBook): FormValue[] {
  const { SheetNames, Sheets } = workBook;

  if (SheetNames.length < 1) {
    throw new Error(EXCEL_FORM_ERROR_MESSAGE);
  }

  const sheet = Sheets[SheetNames[0]];

  const latestExcelDataRowNumber = findLatestExcelDataRowNumber({
    dataStartRow: DATA_START_ROW,
    formColumnCount: addressExcelForm.length,
    sheet,
  });

  if (latestExcelDataRowNumber < DATA_START_ROW) {
    throw new Error(EXCEL_FORM_ERROR_MESSAGE);
  }

  if (latestExcelDataRowNumber - HEADER_ROW_COUNT > 5000) {
    throw new Error(ERROR_TOO_MUCH_ROWS);
  }

  const ref = sheet['!ref'];

  if (!ref) {
    throw new Error(EXCEL_FORM_ERROR_MESSAGE);
  }

  // 엑셀 시트를 탐색하기 위해 excelSheetRowNumberList 배열을 만듭니다.
  const excelSheetRowNumberList = Array.from(
    { length: latestExcelDataRowNumber },
    (_v, index) => index + 1
  ).filter((rowNumber) => rowNumber >= DATA_START_ROW);

  return excelSheetRowNumberList.map(
    (rowNumber): FormValue =>
      addressExcelForm.map((column, columnIndex): string => {
        // FIXME(@GilHyeon ): index를 직접 사용하므로 map함수 대신 다시 for문으로 바꾸는것 고려하기
        const excelSheetCell = sheet[`${numberToColumnName(columnIndex)}${rowNumber}`];
        let excelSheetCellValue = excelSheetCell
          ? removeZeroWidthWhiteSpace(String(excelSheetCell.w)).trim()
          : '';

        // 기본 전송수단 'EMAIL' | 'KAKAO' 변환
        if (column === 'DEFAULT_FORWARDING_METHOD') {
          if (excelSheetCellValue === '휴대폰') {
            excelSheetCellValue = 'KAKAO';
          }

          if (excelSheetCellValue === '이메일') {
            excelSheetCellValue = 'EMAIL';
          }
        }

        if (column === 'PHONE_NUMBER') {
          /**
           * ZeroWidthSpace를 제거하지 않으면 isMobilePhone에서 휴대폰번호가 아니라고
           * 판단하여 제대로 처리가 안되는 케이스를 발견했습니다.
           * 먼저 특수문자 공백을 제거 후 휴대폰 번호는 숫자만 입력되게 수정합니다.
           */
          const isMobile = isMobilePhone(excelSheetCellValue, 'ko-KR');
          if (isMobile) {
            excelSheetCellValue = excelSheetCellValue.replace(/[^0-9]/g, '');
          }
          // 전화번호가 셀서식 문제로 1012341234 형식으로 입력되면 제일 앞에 '0' 문자를 추가합니다.
          if (isMobilePhone(excelSheetCellValue, 'ko-KR') && excelSheetCellValue.startsWith('1')) {
            excelSheetCellValue = `0${excelSheetCellValue}`;
          }
        }

        /**
         * 엑셀 파일에서 ALT + ENTER 키로 줄바꿈하면 \r\n 으로 줄바꿈이 진행됩니다.
         * 여기서 일반적인 줄바꿈인 \n만 남기고 \r 문자는 제거합니다.
         */
        if (column === 'DESCRIPTION') {
          excelSheetCellValue = excelSheetCellValue.replaceAll(/[\r]/g, '');
        }

        /**
         * 이메일 주소에서 시각적으로 식별이 불가능한 특수문자 '＠'를 키보드로 입력하는 '@'로 변경합니다.
         */
        if (column === 'EMAIL') {
          excelSheetCellValue = excelSheetCellValue.replace(/＠/g, '@');
        }

        return excelSheetCellValue;
      })
  );
}
